import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  Search, 
  ArrowUpDown, 
  ChevronUp, 
  ChevronDown,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Building2,
  Tag,
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";
import { recommendations } from "@/data/opportunities";

const RecommendationBadge = ({ recommendation }) => {
  const styles = {
    "Bid": "bg-bid-bg text-bid border-bid/20",
    "No-Bid": "bg-nobid-bg text-nobid border-nobid/20",
    "Partner": "bg-partner-bg text-partner border-partner/20",
  };

  return (
    <Badge 
      variant="outline" 
      className={cn(
        "font-medium px-2.5 py-0.5 border",
        styles[recommendation] || styles["Bid"]
      )}
    >
      {recommendation}
    </Badge>
  );
};

const RiskBadge = ({ risk }) => {
  const config = {
    "Low": { icon: CheckCircle2, className: "text-success bg-success/10" },
    "Medium": { icon: AlertCircle, className: "text-partner bg-partner/10" },
    "High": { icon: AlertTriangle, className: "text-destructive bg-destructive/10" },
  };

  const { icon: Icon, className } = config[risk] || config["Medium"];

  return (
    <div className={cn("inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium", className)}>
      <Icon className="w-3.5 h-3.5" />
      {risk}
    </div>
  );
};

const ScorePill = ({ score }) => {
  const getScoreStyle = (score) => {
    if (score >= 80) return "bg-score-high text-success-foreground";
    if (score >= 60) return "bg-score-medium text-success-foreground";
    if (score >= 40) return "bg-score-low text-warning-foreground";
    return "bg-score-poor text-destructive-foreground";
  };

  return (
    <div className={cn(
      "inline-flex items-center justify-center w-12 h-7 rounded-full text-sm font-bold",
      getScoreStyle(score)
    )}>
      {score}
    </div>
  );
};

export const OpportunityTable = ({ 
  data, 
  onUpdateRecommendation 
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "score", direction: "desc" });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
    }));
  };

  const handleRowClick = (oppId, e) => {
    // Don't navigate if clicking on the recommendation dropdown
    if (e.target.closest('[data-radix-select-trigger]') || 
        e.target.closest('[role="listbox"]') ||
        e.target.closest('button')) {
      return;
    }
    navigate(`/opportunity/${oppId}`);
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter(opp => 
      opp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.agency.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.tech.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return filtered.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (sortConfig.key === "due") {
        return sortConfig.direction === "asc" 
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }
      
      if (typeof aVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      
      return sortConfig.direction === "asc" 
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [data, searchQuery, sortConfig]);

  const SortHeader = ({ label, sortKey }) => (
    <Button
      variant="ghost"
      size="sm"
      className="h-auto p-0 hover:bg-transparent text-muted-foreground hover:text-foreground font-medium"
      onClick={() => handleSort(sortKey)}
    >
      <span>{label}</span>
      <span className="ml-1.5">
        {sortConfig.key === sortKey ? (
          sortConfig.direction === "asc" ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
        )}
      </span>
    </Button>
  );

  const getRowClassName = (recommendation) => {
    const base = "transition-colors cursor-pointer";
    switch (recommendation) {
      case "Bid":
        return cn(base, "hover:bg-bid-bg/50 data-[selected=true]:bg-bid-bg");
      case "No-Bid":
        return cn(base, "hover:bg-nobid-bg/50 data-[selected=true]:bg-nobid-bg");
      case "Partner":
        return cn(base, "hover:bg-partner-bg/50 data-[selected=true]:bg-partner-bg");
      default:
        return cn(base, "hover:bg-muted/50");
    }
  };

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="text-lg font-semibold font-heading text-foreground">
              Opportunities Pipeline
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-1">
              {filteredAndSortedData.length} opportunities • Click row to view details
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by ID, title, agency, or tech..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 bg-background"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="w-[100px] font-semibold">
                  <SortHeader label="ID" sortKey="id" />
                </TableHead>
                <TableHead className="min-w-[200px] font-semibold">
                  <SortHeader label="Title" sortKey="title" />
                </TableHead>
                <TableHead className="font-semibold">
                  <SortHeader label="Agency" sortKey="agency" />
                </TableHead>
                <TableHead className="font-semibold">Vehicle</TableHead>
                <TableHead className="font-semibold">Tech Area</TableHead>
                <TableHead className="text-center font-semibold">
                  <SortHeader label="Fit Score" sortKey="score" />
                </TableHead>
                <TableHead className="font-semibold">Recommendation</TableHead>
                <TableHead className="font-semibold">
                  <SortHeader label="Due Date" sortKey="due" />
                </TableHead>
                <TableHead className="font-semibold">Risk</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedData.map((opp) => (
                <TableRow 
                  key={opp.id} 
                  className={getRowClassName(opp.recommendation)}
                  onClick={(e) => handleRowClick(opp.id, e)}
                >
                  <TableCell className="font-mono text-xs font-medium text-primary">
                    <div className="flex items-center gap-1.5">
                      {opp.id}
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground">{opp.title}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-medium">{opp.agency}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{opp.vehicle}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm">{opp.tech}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <ScorePill score={opp.score} />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={opp.recommendation}
                      onValueChange={(value) => onUpdateRecommendation(opp.id, value)}
                    >
                      <SelectTrigger className="w-[130px] h-8 text-xs border-0 shadow-none bg-transparent hover:bg-muted/50 focus:ring-0">
                        <SelectValue>
                          <RecommendationBadge recommendation={opp.recommendation} />
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {recommendations.map(rec => (
                          <SelectItem key={rec} value={rec} className="text-sm">
                            <RecommendationBadge recommendation={rec} />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {new Date(opp.due).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </div>
                  </TableCell>
                  <TableCell>
                    <RiskBadge risk={opp.risk} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
